import React, { useState } from 'react';

const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    interest: 'Wedding Jewellery',
    message: '',
  });
  const [hoursExpanded, setHoursExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      // Send email using Resend API through backend endpoint
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: process.env.REACT_APP_CONTACT_EMAIL || 'devraj@nocodeweb.org',
          from: formData.email,
          subject: `New Enquiry from ${formData.fullName}`,
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          interest: formData.interest,
          message: formData.message,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send email');
      }

      // Success
      setSubmitStatus('success');
      // Reset form
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        interest: 'Wedding Jewellery',
        message: '',
      });

      // Hide success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus('error');
      setErrorMessage('Failed to send message. Please try again or contact us directly.');
      
      // Hide error message after 5 seconds
      setTimeout(() => {
        setSubmitStatus('idle');
        setErrorMessage('');
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Special handling for phone number - only allow digits and limit to 10
    if (name === 'phone') {
      // Remove all non-digit characters
      const digitsOnly = value.replace(/\D/g, '');
      // Limit to 10 digits
      const limitedDigits = digitsOnly.slice(0, 10);
      setFormData({
        ...formData,
        [name]: limitedDigits,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  return (
    <section id="contact" className="py-20 px-6 bg-black">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column - Visit Our Showroom */}
          <div>
            <p className="text-yellow-500 text-xs font-bold tracking-[0.3em] uppercase mb-4">
              Contact Us
            </p>
            <h2 className="text-4xl md:text-5xl font-serif text-white mb-6">
              Visit Our Showroom
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              Our concierge team is ready to assist you in selecting the perfect piece of jewellery.
            </p>

            {/* Contact Information */}
            <div className="space-y-6 mb-8">
              {/* Location */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-yellow-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Our Location</h3>
                  <a 
                    href="https://www.google.com/maps/dir//Siyaram+Jewellers,+27,+Shree+Ram+Nagar,+Benad+Road,+Pawanpuri+Rd,+Jaipur,+Rajasthan+302012/@26.9817469,75.7335665,17z"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-yellow-500 transition-colors cursor-pointer"
                  >
                    27, Shree Ram Nagar, Benad Road,<br />Pawanpuri Rd, Jaipur, Rajasthan 302012
                  </a>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-yellow-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">Call Us</h3>
                  <a 
                    href="tel:+919928944736"
                    className="text-gray-400 hover:text-yellow-500 transition-colors cursor-pointer"
                  >
                    099289 44736
                  </a>
                </div>
              </div>

              {/* Hours */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-yellow-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold mb-1">Hours</h3>
                  <button
                    onClick={() => setHoursExpanded(!hoursExpanded)}
                    className="text-gray-400 hover:text-yellow-500 transition-colors flex items-center space-x-2 text-left"
                  >
                    <span>Open · Closes 8 pm</span>
                    <svg 
                      className={`w-4 h-4 transition-transform ${hoursExpanded ? 'rotate-180' : ''}`} 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {hoursExpanded && (
                    <div className="mt-3 space-y-1 text-sm">
                      <div className="flex justify-between text-gray-400">
                        <span>Monday</span>
                        <span>10:00 AM - 8:00 PM</span>
                      </div>
                      <div className="flex justify-between text-gray-400">
                        <span>Tuesday</span>
                        <span>10:00 AM - 8:00 PM</span>
                      </div>
                      <div className="flex justify-between text-gray-400">
                        <span>Wednesday</span>
                        <span>10:00 AM - 8:00 PM</span>
                      </div>
                      <div className="flex justify-between text-gray-400">
                        <span>Thursday</span>
                        <span>10:00 AM - 8:00 PM</span>
                      </div>
                      <div className="flex justify-between text-gray-400">
                        <span>Friday</span>
                        <span>10:00 AM - 8:00 PM</span>
                      </div>
                      <div className="flex justify-between text-gray-400">
                        <span>Saturday</span>
                        <span>10:00 AM - 8:00 PM</span>
                      </div>
                      <div className="flex justify-between text-gray-400">
                        <span>Sunday</span>
                        <span>10:00 AM - 8:00 PM</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Map Placeholder */}
            <a 
              href="https://www.google.com/maps/dir//Siyaram+Jewellers,+27,+Shree+Ram+Nagar,+Benad+Road,+Pawanpuri+Rd,+Jaipur,+Rajasthan+302012/@26.9817469,75.7335665,17z"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full h-64 bg-gray-800 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
            >
              <img 
                src="https://images.unsplash.com/photo-1524661135-423995f22d0b?w=600&h=400&fit=crop" 
                alt="Siyaram Jewellers store location - Click to open in Google Maps" 
                className="w-full h-full object-cover opacity-50"
              />
            </a>
          </div>

          {/* Right Column - Contact Form */}
          <div className="flex items-center">
            <div className="bg-gradient-to-br from-gray-900/80 to-black/80 border border-gray-800/50 rounded-2xl p-8 backdrop-blur-sm w-full">
              <h3 className="text-2xl font-serif text-white mb-6">Send an Enquiry</h3>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Success Message */}
                {submitStatus === 'success' && (
                  <div className="bg-green-500/20 border border-green-500 text-green-400 px-4 py-3 rounded-lg text-sm">
                    Thank you for your enquiry! We will get back to you soon.
                  </div>
                )}

                {/* Error Message */}
                {submitStatus === 'error' && (
                  <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg text-sm">
                    {errorMessage}
                  </div>
                )}

                {/* Name and Email */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="fullName" className="block text-gray-500 text-xs font-semibold mb-2 uppercase tracking-wider">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="John Doe"
                      required
                      className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500/50 transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-gray-500 text-xs font-semibold mb-2 uppercase tracking-wider">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                      className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500/50 transition-all"
                    />
                  </div>
                </div>

                {/* Phone Number */}
                <div>
                  <label htmlFor="phone" className="block text-gray-500 text-xs font-semibold mb-2 uppercase tracking-wider">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="9999999999"
                    required
                    pattern="[0-9]{10}"
                    minLength={10}
                    maxLength={10}
                    title="Please enter exactly 10 digits (Indian mobile number)"
                    className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500/50 transition-all"
                  />
                  {formData.phone && formData.phone.length !== 10 && (
                    <p className="text-red-400 text-xs mt-1">
                      Phone number must be exactly 10 digits
                    </p>
                  )}
                </div>

                {/* Interest Dropdown */}
                <div>
                  <label htmlFor="interest" className="block text-gray-500 text-xs font-semibold mb-2 uppercase tracking-wider">
                    Interested In
                  </label>
                  <select
                    id="interest"
                    name="interest"
                    value={formData.interest}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-gray-100 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500/50 transition-all"
                  >
                    <option value="Wedding Jewellery">Wedding Jewellery</option>
                    <option value="Gold Jewellery">Gold Jewellery</option>
                    <option value="Diamond Jewellery">Diamond Jewellery</option>
                    <option value="Silver Jewellery">Silver Jewellery</option>
                    <option value="Custom Design">Custom Design</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-gray-500 text-xs font-semibold mb-2 uppercase tracking-wider">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="How can we help you today?"
                    rows={4}
                    className="w-full px-4 py-3 bg-black/50 border border-gray-700 rounded-lg text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500/50 transition-all resize-none"
                  ></textarea>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold py-4 px-6 rounded-lg transition-all duration-300 uppercase text-sm tracking-wider shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
